import { supportedLocales, type RuntimeMode } from "./foundation.js";

export const AGENTIC_TOOL_ALLOWLIST = [
  "TRIP_CONTEXT_READ",
  "PLANNER_EVALUATE",
  "DISCOVERY_SEARCH",
  "SUPPORT_GUIDANCE"
] as const;

export type AgenticToolName = (typeof AGENTIC_TOOL_ALLOWLIST)[number];
export type MicrophonePermission = "PROMPT" | "GRANTED" | "DENIED" | "RESTRICTED";
export type SupportEscalationCategory =
  | "PAYMENT_DISPUTE"
  | "BOOKING_OR_REFUND"
  | "PAYOUT"
  | "CHILD_SAFETY"
  | "MODERATION"
  | "APPEAL"
  | "OTHER_UNRESOLVED";

export interface AgenticTurnRequest {
  readonly requestId: string;
  readonly accountId: string | null;
  readonly prompt: string;
  readonly locale: string;
  readonly userInitiated: true;
  readonly allowedTripIds: readonly string[];
  readonly runtime: RuntimeMode;
}

export interface AgenticToolRequest {
  readonly tool: string;
  readonly arguments: Readonly<Record<string, unknown>>;
  readonly modelOutputUntrusted: true;
}

export interface AuthorizedAgenticToolRequest {
  readonly tool: AgenticToolName;
  readonly arguments: Readonly<Record<string, unknown>>;
  readonly authority: "SERVER";
  readonly consequentialMutationAllowed: false;
}

export interface SpeechCaptureDecision {
  readonly captureAllowed: boolean;
  readonly mode: "PUSH_TO_TALK";
  readonly alwaysOnMicrophone: false;
  readonly typedFallbackAvailable: true;
  readonly requestPermissionInContext: boolean;
  readonly denialReason: "NONE" | "USER_GESTURE_REQUIRED" | "PERMISSION_REQUIRED" | "PERMISSION_DENIED";
}

export interface TranslationGlossaryEntry {
  readonly source: string;
  readonly approvedTranslation: string;
}

const toolSet = new Set<string>(AGENTIC_TOOL_ALLOWLIST);
const prohibitedArgumentKeys = new Set(["__proto__", "constructor", "prototype"]);

function requireText(value: string, field: string, maximumLength = 8_000) {
  const normalized = value.trim();
  if (normalized.length === 0) throw new Error(`${field} must not be blank`);
  if (normalized.length > maximumLength) throw new Error(`${field} exceeds the ${maximumLength}-character limit`);
  return normalized;
}

function canonicalIso(value: string, field: string) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString() !== value) throw new Error(`${field} must be canonical ISO`);
  return value;
}

function validateStructuredArguments(value: unknown, depth = 0): void {
  if (depth > 8) throw new Error("Agentic tool arguments exceed the maximum nesting depth");
  if (value === null || typeof value === "string" || typeof value === "boolean") return;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("Agentic tool arguments require finite numbers");
    return;
  }
  if (Array.isArray(value)) {
    if (value.length > 100) throw new Error("Agentic tool argument arrays are too large");
    for (const entry of value) validateStructuredArguments(entry, depth + 1);
    return;
  }
  if (typeof value !== "object") throw new Error("Agentic tool arguments must be JSON-compatible");
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) throw new Error("Agentic tool arguments must be plain objects");
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length > 50) throw new Error("Agentic tool argument objects are too large");
  for (const [key, entry] of entries) {
    if (prohibitedArgumentKeys.has(key)) throw new Error("Agentic tool arguments contain a prohibited key");
    validateStructuredArguments(entry, depth + 1);
  }
}

export function createAgenticTurnRequest(input: AgenticTurnRequest): AgenticTurnRequest {
  requireText(input.requestId, "requestId", 128);
  requireText(input.prompt, "prompt");
  if (input.accountId !== null) requireText(input.accountId, "accountId", 128);
  if (!(supportedLocales as readonly string[]).includes(input.locale)) throw new Error("Agentic locale is not supported in V1");
  if (input.userInitiated !== true) throw new Error("Agentic must remain quiet unless the user opens or invokes it");
  if (new Set(input.allowedTripIds).size !== input.allowedTripIds.length) throw new Error("Agentic trip context must not contain duplicates");
  input.allowedTripIds.forEach((tripId) => requireText(tripId, "allowedTripId", 128));
  return Object.freeze({ ...input, allowedTripIds: Object.freeze([...input.allowedTripIds]) });
}

export function authorizeAgenticToolRequest(turn: AgenticTurnRequest, request: AgenticToolRequest): AuthorizedAgenticToolRequest {
  createAgenticTurnRequest(turn);
  if (request.modelOutputUntrusted !== true) throw new Error("Model tool output must be treated as untrusted");
  if (!toolSet.has(request.tool)) throw new Error(`Agentic tool is not allowlisted: ${request.tool}`);
  validateStructuredArguments(request.arguments);
  if (request.tool === "TRIP_CONTEXT_READ") {
    const tripId = request.arguments.tripId;
    if (turn.accountId === null) throw new Error("Trip context requires an authenticated account");
    if (typeof tripId !== "string" || !turn.allowedTripIds.includes(tripId)) throw new Error("Agentic cannot read trip context outside the authorized trip set");
  }
  return Object.freeze({
    tool: request.tool as AgenticToolName,
    arguments: Object.freeze({ ...request.arguments }),
    authority: "SERVER" as const,
    consequentialMutationAllowed: false as const
  });
}

export function decideSpeechCapture(input: { readonly userPressedMicrophone: boolean; readonly permission: MicrophonePermission }): SpeechCaptureDecision {
  if (!input.userPressedMicrophone) {
    return Object.freeze({ captureAllowed: false, mode: "PUSH_TO_TALK", alwaysOnMicrophone: false, typedFallbackAvailable: true, requestPermissionInContext: false, denialReason: "USER_GESTURE_REQUIRED" });
  }
  if (input.permission === "PROMPT") {
    return Object.freeze({ captureAllowed: false, mode: "PUSH_TO_TALK", alwaysOnMicrophone: false, typedFallbackAvailable: true, requestPermissionInContext: true, denialReason: "PERMISSION_REQUIRED" });
  }
  if (input.permission !== "GRANTED") {
    return Object.freeze({ captureAllowed: false, mode: "PUSH_TO_TALK", alwaysOnMicrophone: false, typedFallbackAvailable: true, requestPermissionInContext: false, denialReason: "PERMISSION_DENIED" });
  }
  return Object.freeze({ captureAllowed: true, mode: "PUSH_TO_TALK", alwaysOnMicrophone: false, typedFallbackAvailable: true, requestPermissionInContext: false, denialReason: "NONE" });
}

export function decideTtsOutput(input: { readonly userOptedIn: boolean; readonly providerAvailable: boolean; readonly disclosureAcknowledged: boolean }) {
  const enabled = input.userOptedIn && input.providerAvailable && input.disclosureAcknowledged;
  return Object.freeze({
    enabled,
    optional: true as const,
    disclosedAiVoiceRequired: true as const,
    textResponseAlwaysAvailable: true as const,
    reason: enabled ? "ENABLED" as const : !input.userOptedIn ? "NOT_REQUESTED" as const : !input.disclosureAcknowledged ? "DISCLOSURE_REQUIRED" as const : "PROVIDER_UNAVAILABLE" as const
  });
}

export function createTranslationRequest(input: { readonly requestId: string; readonly text: string; readonly sourceLocale: string; readonly targetLocale: string; readonly glossary: readonly TranslationGlossaryEntry[] }) {
  requireText(input.requestId, "requestId", 128);
  requireText(input.text, "text", 20_000);
  if (!(supportedLocales as readonly string[]).includes(input.sourceLocale) || !(supportedLocales as readonly string[]).includes(input.targetLocale)) {
    throw new Error("Translation locales must use the approved V1 language set");
  }
  if (input.sourceLocale === input.targetLocale) throw new Error("Translation source and target locales must differ");
  const seen = new Set<string>();
  const glossary = input.glossary.map((entry) => {
    const source = requireText(entry.source, "glossary source", 128);
    const approvedTranslation = requireText(entry.approvedTranslation, "glossary translation", 128);
    const key = source.toLocaleLowerCase(input.sourceLocale);
    if (seen.has(key)) throw new Error(`Duplicate translation glossary term: ${source}`);
    seen.add(key);
    return Object.freeze({ source, approvedTranslation });
  });
  return Object.freeze({ ...input, text: input.text.trim(), glossary: Object.freeze(glossary), glossaryMustBePreserved: true as const });
}

export function createSupportEscalation(input: { readonly escalationId: string; readonly accountId: string; readonly category: SupportEscalationCategory; readonly summary: string; readonly requestedAt: string; readonly userEmail: string }) {
  requireText(input.escalationId, "escalationId", 128);
  requireText(input.accountId, "accountId", 128);
  requireText(input.summary, "summary", 4_000);
  canonicalIso(input.requestedAt, "requestedAt");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.userEmail)) throw new Error("Support escalation requires a valid reply email");
  return Object.freeze({
    ...input,
    status: "PENDING_EMAIL_HANDOFF" as const,
    humanEscalationRequired: true as const,
    phoneSupportAvailable: false as const,
    providerSubmissionAllowed: false as const,
    transportCapabilityRequired: "support_email:outbound_email" as const
  });
}

